import { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  BrowserRouter as Router,
  useLocation,
  useHistory,
} from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { Box, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import RouterConfig from '@sites/navigation/RouterConfig';
import useThemeConfig from '@shared/theme';
import './ws';
import useVersionControl from '@shared/hooks/useVersionControl';
import useRestoreSearchFromStorage from '@shared/hooks/useRestoreSearchFromStorage';
import {
  ASSIST_SEARCH,
  FLIGHTS_SEARCH,
  HOTELS_SEARCH,
  HOTELS,
  ASSIST,
  FLIGHTS,
} from '@shared/helpers/routes';
import { GoogleMapsProvider } from '@shared/providers/GoogleMapsProvider';

import { onUserLogin, onUserLogout } from '@shared/redux/actions';
import { NAME_SESSION } from '@shared/helpers/auth';

const searchPaths = [ASSIST_SEARCH, FLIGHTS_SEARCH, HOTELS_SEARCH];

const PRODUCT_ROUTES = [
  { basePath: FLIGHTS, redirect: FLIGHTS },
  { basePath: ASSIST, redirect: ASSIST },
  { basePath: HOTELS, redirect: HOTELS },
];

const getInitialProductPath = pathname => {
  const product = PRODUCT_ROUTES.find(({ basePath }) =>
    pathname.startsWith(basePath)
  );

  return product?.redirect || HOTELS;
};

const AppContent = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const dispatch = useDispatch();

  const history = useHistory();

  const { checkVersion } = useVersionControl(pathname);

  useEffect(() => {
    const isSearchPath = searchPaths.some(p => pathname.startsWith(p));
    if (!isSearchPath) {
      checkVersion({
        type: 'router',
        infoSearch: null,
        flightSearchType: '',
      });
    }
  }, [pathname, checkVersion]);

  useEffect(() => {
    const handleStorage = event => {
      if (event.key !== NAME_SESSION) {
        return;
      }

      if (event.newValue) {
        const session = JSON.parse(event.newValue);

        dispatch(onUserLogin(session));
        return;
      }

      dispatch(onUserLogout());

      history.push(getInitialProductPath(window.location.pathname));
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch, history]);

  return <RouterConfig />;
};

const updateMetaTags = (title, imageUrl, url) => {
  if (title) {
    let metaTitle = document.querySelector('meta[property="og:title"]');
    if (!metaTitle) {
      metaTitle = document.createElement('meta');
      metaTitle.setAttribute('property', 'og:title');
      document.getElementsByTagName('head')[0].appendChild(metaTitle);
    }
    metaTitle.setAttribute('content', title);
  }
  if (imageUrl) {
    let metaImage = document.querySelector('meta[property="og:image"]');
    if (!metaImage) {
      metaImage = document.createElement('meta');
      metaImage.setAttribute('property', 'og:image');
      document.getElementsByTagName('head')[0].appendChild(metaImage);
    }
    metaImage.setAttribute('content', imageUrl);
  }
  if (url) {
    let metaUrl = document.querySelector('meta[property="og:url"]');
    if (!metaUrl) {
      metaUrl = document.createElement('meta');
      metaUrl.setAttribute('property', 'og:url');
      document.getElementsByTagName('head')[0].appendChild(metaUrl);
    }
    metaUrl.setAttribute('content', url);
  }
};

const updateFavicon = iconUrl => {
  const link =
    document.querySelector("link[rel*='icon']") ||
    document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'icon';
  link.href = iconUrl;
  document.getElementsByTagName('head')[0].appendChild(link);
};

const App = () => {
  const tagManagerArgs = { gtmId: 'GTM-KMJ6D7Q' };

  const pageConfiguration = useSelector(store => store.pageConfiguration);
  const restored = useRestoreSearchFromStorage();
  const { theme, isDataLoaded } = useThemeConfig();

  const ready = isDataLoaded && restored;

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    if (pageConfiguration?.site_logos) {
      updateFavicon(pageConfiguration.site_logos.favicon);
      updateMetaTags(
        pageConfiguration.name,
        pageConfiguration.site_logos?.banner || '',
        pageConfiguration.url
      );
      document.title = pageConfiguration.description;
    }
  }, [pageConfiguration]);

  if (!ready || !theme) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <GoogleMapsProvider>
        <Router>
          <AppContent />
        </Router>
      </GoogleMapsProvider>
    </ThemeProvider>
  );
};

export default App;
